import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Plus, Search, Eye, Edit2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, Upload } from 'lucide-react';

const ROLES = ['Wire Saw Operator','LD-4 Operator','Driller','Cutter','Poclain Operator','Tipper Driver','Supervisor','Site Manager','Helper','Other'];
const DRIVER_ROLES = ['Poclain Operator','Tipper Driver'];
const SKILL_LEVELS = [
  { val:'UNSKILLED', label:'Unskilled (Beginner)' },
  { val:'SEMI_SKILLED', label:'Semi-Skilled' },
  { val:'SKILLED', label:'Skilled' },
  { val:'HIGHLY_SKILLED', label:'Highly Skilled (Expert)' },
  { val:'SUPERVISOR', label:'Supervisor' },
];
const BLOOD_GROUPS = ['A+','A-','B+','B-','O+','O-','AB+','AB-'];

const EMPTY = {
  first_name:'', last_name:'', phone:'', alternate_phone:'',
  emergency_contact_name:'', address:'', blood_group:'', date_of_birth:'',
  designation:'', department:'', skill_level:'UNSKILLED',
  employment_type:'PERMANENT', contractor_company:'', vehicle_number:'', contract_rate:'', contract_rate_type:'',
  wage_type:'DAILY', wage_amount:'', overtime_rate:'', rate_per_unit:'',
  date_of_joining: new Date().toISOString().split('T')[0],
  is_active:true, driving_license:'', license_expiry:'',
  aadhar_number:'', pan_number:'', pf_number:'', esi_number:'',
  bank_name:'', bank_account_number:'', bank_ifsc:'', email:'', biometric_id:'',
};

const isDriver = r => DRIVER_ROLES.includes(r);
const licenseExpired = d => d && new Date(d) < new Date();

const statusBadge = emp => emp.is_active
  ? <span className="badge badge-green">Active</span>
  : <span className="badge badge-red">Inactive</span>;

const skillBadge = s => {
  const map = { UNSKILLED:'badge-muted', SEMI_SKILLED:'badge-blue', SKILLED:'badge-blue', HIGHLY_SKILLED:'badge-purple', SUPERVISOR:'badge-gold' };
  return <span className={`badge ${map[s]||'badge-muted'}`}>{s?.replace(/_/g,' ')||'—'}</span>;
};

// Section component — defined OUTSIDE to prevent cursor jump
const Section = ({ title, children, defaultOpen=true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom:14, border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflow:'hidden' }}>
      <button type="button" onClick={() => setOpen(o=>!o)} style={{
        width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'10px 16px', background:'var(--bg-primary)', border:'none', cursor:'pointer',
      }}>
        <span style={{ fontSize:12, fontWeight:700, color:'var(--brand)', textTransform:'uppercase', letterSpacing:'0.8px' }}>{title}</span>
        {open ? <ChevronUp size={14} style={{ color:'var(--text-muted)' }}/> : <ChevronDown size={14} style={{ color:'var(--text-muted)' }}/>}
      </button>
      {open && <div style={{ padding:16 }}><div className="form-grid">{children}</div></div>}
    </div>
  );
};

const Field = ({ label, required, full, children }) => (
  <div className="form-group" style={full?{gridColumn:'1/-1'}:{}}>
    <label className="form-label">{label}{required && <span style={{ color:'var(--red)', marginLeft:3 }}>*</span>}</label>
    {children}
  </div>
);

export default function EmployeesPage() {
  const [employees, setEmployees]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRole, setFilterRole]     = useState('');
  const [view, setView]                 = useState('list');
  const [selected, setSelected]         = useState(null);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [msg, setMsg]                   = useState('');
  const [sameContact, setSameContact]   = useState(false);
  const [detailTab, setDetailTab] = useState('Profile');
  const [workHistory, setWorkHistory] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [salaryList, setSalaryList] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [detailMonth, setDetailMonth] = useState(new Date().toISOString().slice(0,7));
  const today = new Date().toISOString().split('T')[0];
  const currentMonth = new Date().toISOString().slice(0,7);
  const [showAttModal, setShowAttModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showAdvModal, setShowAdvModal] = useState(false);
  const [tabSaving, setTabSaving] = useState(false);
  const [attForm, setAttForm] = useState({date:new Date().toISOString().split('T')[0],status:'PRESENT',shift:'MORNING',notes:''});
  const [leaveForm, setLeaveForm] = useState({leave_type:'SICK',from_date:new Date().toISOString().split('T')[0],to_date:new Date().toISOString().split('T')[0],days:1,reason:'',status:'APPROVED'});
  const [salaryForm, setSalaryForm] = useState({payment_type:'MONTHLY',payment_mode:'CASH',amount:'',month:new Date().toISOString().slice(0,7),days_worked:'',days_absent:'',advance_deducted:'0',deductions:'0',notes:''});
  const [advForm, setAdvForm] = useState({amount:'',reason:'',deduct_month:'',notes:''});

  // Form state — fully controlled (no refs) to fix save button issues
  const [form, setForm] = useState(EMPTY);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  // Document uploads
  const [addressDoc, setAddressDoc]   = useState(null);
  const [addressDocType, setAddressDocType] = useState('AADHAR');
  const [licenseDoc, setLicenseDoc]   = useState(null);
  const addressFileRef = useRef();
  const licenseFileRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    let url = `/employees/?search=${search}&page_size=100`;
    if (filterStatus==='active')   url += '&is_active=true';
    if (filterStatus==='inactive') url += '&is_active=false';
    api.get(url).then(r => setEmployees(r.data.results||[])).finally(() => setLoading(false));
  }, [search, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY); setError(''); setSameContact(false);
    setAddressDoc(null); setLicenseDoc(null);
    setSelected(null); setView('form');
  };

  const openEdit = async emp => {
    setError('');
    try {
      const res = await api.get(`/employees/${emp.id}/`);
      const full = res.data;
      setForm({
        ...EMPTY,
        ...full,
        first_name:             full.first_name             || '',
        last_name:              full.last_name              || '',
        phone:                  full.phone                  || '',
        alternate_phone:        full.alternate_phone        || '',
        emergency_contact_name: full.emergency_contact_name || '',
        address:                full.address                || '',
        blood_group:            full.blood_group            || '',
        date_of_birth:          full.date_of_birth          || '',
        designation:            full.designation            || '',
        department:             full.department             || '',
        skill_level:            full.skill_level            || 'UNSKILLED',
        employment_type:    full.employment_type    || 'PERMANENT',
        contractor_company: full.contractor_company || '',
        vehicle_number:     full.vehicle_number     || '',
        contract_rate:      full.contract_rate      || '',
        contract_rate_type: full.contract_rate_type || '',
        wage_type:              full.wage_type              || 'DAILY',
        wage_amount:            full.wage_amount            || '',
        overtime_rate:          full.overtime_rate          || '',
        rate_per_unit:          full.rate_per_unit          || '',
        date_of_joining:        full.date_of_joining        || '',
        driving_license:        full.driving_license        || '',
        license_expiry:         full.license_expiry         || '',
        aadhar_number:          full.aadhar_number          || '',
        pan_number:             full.pan_number             || '',
        pf_number:              full.pf_number              || '',
        esi_number:             full.esi_number             || '',
        bank_name:              full.bank_name              || '',
        bank_account_name:      full.bank_account_name      || '',
        bank_account_number:    full.bank_account_number    || '',
        bank_ifsc:              full.bank_ifsc              || '',
        bank_branch:            full.bank_branch            || '',
        email:                  full.email                  || '',
        biometric_id:           full.biometric_id           || '',
        is_active:              full.is_active !== undefined ? full.is_active : true,
      });
      setSameContact(false);
      setAddressDoc(null); setLicenseDoc(null);
      setSelected(full); setView('form');
    } catch (err) {
      setError('Failed to load employee details.');
    }
  };

  const openDetail = async emp => {
    setDetailTab('Profile');
    try {
      const res = await api.get(`/employees/${emp.id}/`);
      const empData = res.data?.data || res.data;
      try {
        const docRes = await api.get(`/employees/${emp.id}/documents/`);
        empData.documents = docRes.data.results || docRes.data || [];
      } catch { empData.documents = []; }
      setSelected(empData);
      loadDetailData(emp.id);
      loadAttendance(emp.id, detailMonth); 
    } catch { setSelected(emp); }
    setView('detail');
  };

const loadDetailData = async (empId) => {
  try {
    const [whr, lr, salr, advr] = await Promise.all([
      api.get(`/operations/sessions/?page_size=200`),
      api.get(`/employees/leaves/?employee=${empId}`),
      api.get(`/employees/salary/?employee=${empId}`),
      api.get(`/employees/advances/?employee=${empId}`),
    ]);
    setWorkHistory(whr.data.results||[]);
    setLeaves(lr.data.results||[]);
    setSalaryList(salr.data.results||[]);
    setAdvances(advr.data.results||[]);
  } catch(e) { console.error(e); }
};

const loadAttendance = async (empId, month) => {
  try {
    const r = await api.get(`/employees/attendance/?employee=${empId}&month=${month}`);
    setAttendance(r.data.results||[]);
  } catch {}
};

  const handleStatusChange = async emp => {
    try {
      await api.patch(`/employees/${emp.id}/`, { is_active: !emp.is_active });
      setMsg(`${emp.first_name} status updated`);
      setTimeout(() => setMsg(''), 3000);
      load();
    } catch {}
  };

  const uploadDoc = async (empId, file, docType) => {
    if (!file || !empId) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    fd.append('title', docType);
    try {
      await api.post(`/employees/${empId}/documents/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (e) {
      console.log('Doc upload error:', e);
    }
  };

  const handleSubmit = async (e, addNew=false) => {
    e.preventDefault();

    // Validations
    if (!/^\d{10}$/.test(form.phone)) {
      setError('Contact number must be exactly 10 digits.'); return;
    }
    if (isDriver(form.designation) && !form.driving_license) {
      setError('Driving License Number is required for this role.'); return;
    }

    setSaving(true); setError('');

    try {
      let empId;
      // Clean up empty date fields before saving
      const cleanForm = {...form};
      if(!cleanForm.license_expiry) cleanForm.license_expiry = null;
      if(!cleanForm.date_of_birth) cleanForm.date_of_birth = null;
      if(!cleanForm.date_of_leaving) cleanForm.date_of_leaving = null;
      if (selected?.id) {
        await api.patch(`/employees/${selected.id}/`, cleanForm);
        empId = selected.id;
      } else {
        const res = await api.post('/employees/', cleanForm);
        empId = res.data?.data?.id || res.data?.id;
      }

      // Upload address proof document
      if (addressDoc && empId) {
        await uploadDoc(empId, addressDoc, addressDocType);
      }

      // Upload driving license document
      if (licenseDoc && empId) {
        await uploadDoc(empId, licenseDoc, 'DRIVING_LICENSE');
      }

      setMsg(selected?.id ? 'Employee updated successfully!' : 'Employee saved successfully!');
      setTimeout(() => setMsg(''), 3000);
      load();

      if (addNew) {
        // Reset form for next entry
        setForm(EMPTY);
        setSameContact(false);
        setAddressDoc(null);
        setLicenseDoc(null);
        setSelected(null);
        setError('');
        // Scroll to top
        window.scrollTo(0, 0);
      } else {
        setView('list');
      }
    } catch (err) {
      const errs = err.response?.data?.errors;
      setError(errs
        ? Object.entries(errs).map(([k,v])=>`${k}: ${Array.isArray(v)?v[0]:v}`).join(' | ')
        : 'Failed to save. Please check all fields.');
    } finally {
      setSaving(false);
    }
  };

  // ── Detail View ────────────────────────────────────────────────────────────
  
  if (view === 'detail' && selected) {
    const expired = isDriver(selected.designation) && licenseExpired(selected.license_expiry);
    const inr = v => `₹${Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
    const DETAIL_TABS = ['Profile','Work History','Attendance','Salary','Advance','Leave'];

    const attPresent  = attendance.filter(a=>a.status==='PRESENT').length;
    const attAbsent   = attendance.filter(a=>a.status==='ABSENT').length;
    const attHalf     = attendance.filter(a=>a.status==='HALF_DAY').length;
    const attLeave    = attendance.filter(a=>a.status==='LEAVE').length;
    const totalPaid   = salaryList.filter(s=>s.payment_type!=='ADVANCE').reduce((s,p)=>s+parseFloat(p.net_amount||0),0);
    const pendingAdv  = advances.filter(a=>a.status==='APPROVED').reduce((s,a)=>s+parseFloat(a.amount||0),0);

    const filteredWork = workHistory.filter(s => {
      try {
        const notes = JSON.parse(s.notes||'{}');
        return (notes.day_entries||[]).some(entry => {
          const machineData = entry.data?.machine_data || {};
          return Object.values(machineData).some(machine => 
            machine?.operator?.operator_name === selected.full_name
          );
        });
      } catch { return false; }
    });

    return (
      <Layout title="Employee Details" subtitle={selected.employee_id}
        actions={<button className="btn btn-secondary" onClick={() => setView('list')}>← Back</button>}
      >
        <div style={{ maxWidth:800 }}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:'var(--brand)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:20, color:'#fff' }}>
                {selected.first_name?.[0]}{selected.last_name?.[0]}
              </div>
              <div>
                <div style={{ fontSize:18, fontWeight:700 }}>{selected.full_name}</div>
                <div style={{ fontSize:12, color:'var(--brand)', marginTop:2 }}>{selected.employee_id} · {selected.designation}</div>
              </div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(selected)}><Edit2 size={13}/>Edit</button>
          </div>

          {expired && (
            <div className="error-box" style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <AlertTriangle size={15} style={{ flexShrink:0 }}/>
              Driving License EXPIRED on {selected.license_expiry}. Please renew immediately.
            </div>
          )}

          {/* KPIs */}
          <div className="kpi-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:16 }}>
            <div className="kpi-card green"><div className="kpi-value">{attPresent}</div><div className="kpi-label">Present</div></div>
            <div className="kpi-card red"><div className="kpi-value">{attAbsent}</div><div className="kpi-label">Absent</div></div>
            <div className="kpi-card blue"><div className="kpi-value">{inr(totalPaid)}</div><div className="kpi-label">Total Paid</div></div>
            <div className="kpi-card amber"><div className="kpi-value">{inr(pendingAdv)}</div><div className="kpi-label">Pending Advance</div></div>
          </div>

          {/* Month Selector */}
          <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)' }}>Month:</label>
            <input className="form-input" type="month" value={detailMonth} style={{ width:160 }}
              onChange={e => { setDetailMonth(e.target.value); loadAttendance(selected.id, e.target.value); }}/>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:0, marginBottom:16, border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflow:'hidden', width:'fit-content', flexWrap:'wrap' }}>
            {DETAIL_TABS.map(t=>(
              <button key={t} onClick={()=>setDetailTab(t)} style={{ padding:'8px 16px', fontSize:12, fontWeight:500, background:detailTab===t?'var(--brand)':'var(--white)', color:detailTab===t?'#fff':'var(--text-secondary)', border:'none', cursor:'pointer', borderRight:'1px solid var(--border)' }}>
                {t}
              </button>
            ))}
          </div>

          {/* PROFILE TAB */}
          {detailTab==='Profile' && (
            <div>
              {[
                { title:'Personal', rows:[['Employee ID',selected.employee_id],['Blood Group',selected.blood_group||'—'],['Phone',selected.phone],['Emergency Contact',`${selected.emergency_contact_name||'—'} · ${selected.alternate_phone||'—'}`],['Email',selected.email||'—'],['Address',selected.address||'—']] },
                { title:'Job Details', rows:[['Designation',selected.designation],['Department',selected.department||'—'],['Skill Level',selected.skill_level?.replace(/_/g,' ')],['Status',selected.is_active?'Active':'Inactive'],['Joining Date',selected.date_of_joining],['Biometric ID',selected.biometric_id||'—']] },
                { title:'Salary', rows:[['Wage Type',selected.wage_type],['Amount',`₹${Number(selected.wage_amount||0).toLocaleString('en-IN')}`],['Overtime Rate',selected.overtime_rate?`₹${selected.overtime_rate}/hr`:'—']] },
                ...(isDriver(selected.designation)?[{ title:'Driving License', rows:[['License #',selected.driving_license||'—'],['Expiry',selected.license_expiry||'—']] }]:[]),
                { title:'Documents', rows:[['Aadhar',selected.aadhar_number||'—'],['PAN',selected.pan_number||'—'],['Voter ID',selected.voter_id||'—'],['PF',selected.pf_number||'—'],['ESI',selected.esi_number||'—']] },
                { title:'Bank', rows:[['Bank',selected.bank_name||'—'],['Account Holder',selected.bank_account_name||'—'],['Account Number',selected.bank_account_number||'—'],['Branch',selected.bank_branch||'—'],['IFSC',selected.bank_ifsc||'—']] },
              ].map((sec,si)=>(
                <div key={si} className="card" style={{ marginBottom:12 }}>
                  <div className="card-header" style={{ marginBottom:10 }}><div className="card-title">{sec.title}</div></div>
                  {sec.rows.map(([label,val],i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:i<sec.rows.length-1?'1px solid var(--border-light)':'none' }}>
                      <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', minWidth:160 }}>{label}</span>
                      <span style={{ fontSize:13, color:'var(--text-primary)', textAlign:'right' }}>{val}</span>
                    </div>
                  ))}
                </div>
              ))}
              {selected?.documents?.length > 0 && (
                <div className="card" style={{ marginBottom:12 }}>
                  <div className="card-header" style={{ marginBottom:10 }}><div className="card-title">Uploaded Documents</div></div>
                  {selected.documents.map((doc,i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
                      <span style={{ fontSize:13, fontWeight:500 }}>{doc.doc_type} — {doc.title}</span>
                      <a href={doc.file.startsWith('http') ? doc.file : `http://127.0.0.1:8000${doc.file}`} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'var(--brand)', fontWeight:600 }}>View File</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* WORK HISTORY TAB */}
          {detailTab==='Work History' && (
            <div className="card">
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>Work History</div>
              {filteredWork.length===0 ? (
                <div style={{ textAlign:'center', padding:30, color:'var(--text-muted)' }}>No work sessions found for {selected.full_name}.</div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table>
                    <thead><tr><th>Date</th><th>Shift</th><th>Direction</th><th>Stage</th><th>Reference</th><th>Work Type</th><th>Status</th></tr></thead>
                    <tbody>
                      {filteredWork.map((s,i)=>(
                        <tr key={i}>
                          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{s.started_date}</td>
                          <td style={{ fontSize:12 }}>{s.shift}</td>
                          <td style={{ fontWeight:600, color:'var(--brand)' }}>{s.direction||'—'}</td>
                          <td style={{ fontSize:12, fontWeight:600 }}>{s.production_stage}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--brand)' }}>{s.block_reference}</td>
                          <td style={{ fontSize:12 }}>{s.operation_type}</td>
                          <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:500, background:s.status==='COMPLETED'?'#EAF3DE':'#FAEEDA', color:s.status==='COMPLETED'?'#3B6D11':'#854F0B' }}>{s.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ATTENDANCE TAB */}
          {detailTab==='Attendance' && (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, flex:1 }}>Attendance — {detailMonth}</div>
                <button className="btn btn-primary btn-sm" onClick={()=>setShowAttModal(true)}>+ Mark Attendance</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
                {[['Present',attPresent,'var(--green)'],['Absent',attAbsent,'var(--red)'],['Half Day',attHalf,'var(--amber)'],['Leave',attLeave,'var(--brand)']].map(([l,v,c])=>(
                  <div key={l} style={{ textAlign:'center', padding:'10px', background:'var(--bg-primary)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
                    <div style={{ fontFamily:'DM Mono,monospace', fontSize:22, fontWeight:500, color:c }}>{v}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>{l}</div>
                  </div>
                ))}
              </div>
              {attendance.length===0 ? (
                <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>No attendance for {detailMonth}.</div>
              ) : (
                <table>
                  <thead><tr><th>Date</th><th>Status</th><th>Shift</th><th>Notes</th></tr></thead>
                  <tbody>
                    {attendance.map((a,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{a.date}</td>
                        <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:500, background:{PRESENT:'#EAF3DE',ABSENT:'#FDE8E8',HALF_DAY:'#FAEEDA',LEAVE:'#F0E6FB',OVERTIME:'#E6F1FB'}[a.status]||'#f0f0f0', color:{PRESENT:'#3B6D11',ABSENT:'#C0392B',HALF_DAY:'#854F0B',LEAVE:'#6B2FA0',OVERTIME:'#185FA5'}[a.status]||'#666' }}>{a.status?.replace(/_/g,' ')}</span></td>
                        <td style={{ fontSize:12 }}>{a.shift||'—'}</td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{a.notes||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* SALARY TAB */}
          {detailTab==='Salary' && (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, flex:1 }}>Salary History</div>
                <button className="btn btn-primary btn-sm" onClick={()=>setShowSalaryModal(true)}>+ Pay</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
                {[['Monthly Salary',inr(selected.wage_amount||0),'var(--brand)'],['Total Paid',inr(totalPaid),'var(--green)'],['Pending Advance',inr(pendingAdv),'var(--red)']].map(([l,v,c])=>(
                  <div key={l} style={{ textAlign:'center', padding:'14px', background:'var(--bg-primary)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
                    <div style={{ fontFamily:'DM Mono,monospace', fontSize:18, fontWeight:500, color:c }}>{v}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>{l}</div>
                  </div>
                ))}
              </div>
              {salaryList.length===0 ? (
                <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>No salary records found.</div>
              ) : (
                <table>
                  <thead><tr><th>Date</th><th>Type</th><th>Month</th><th>Amount</th><th>Advance</th><th>Deductions</th><th>Net</th><th>Mode</th></tr></thead>
                  <tbody>
                    {salaryList.map((s,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{s.payment_date}</td>
                        <td style={{ fontSize:12 }}>{s.payment_type}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{s.month||'—'}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', color:'var(--brand)', fontWeight:600 }}>{inr(s.amount)}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', color:'var(--red)' }}>{inr(s.advance_deducted)}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', color:'var(--red)' }}>{inr(s.deductions)}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', color:'var(--green)', fontWeight:700 }}>{inr(s.net_amount)}</td>
                        <td style={{ fontSize:12 }}>{s.payment_mode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ADVANCE TAB */}
          {detailTab==='Advance' && (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, flex:1 }}>Salary Advance</div>
                <button className="btn btn-primary btn-sm" onClick={()=>setShowAdvModal(true)}>+ Advance</button>
              </div>
              <div style={{ padding:'10px 14px', background:'var(--brand-dim)', borderRadius:'var(--radius-sm)', marginBottom:14, fontSize:12, color:'var(--brand)', fontWeight:600 }}>
                Max ₹3,000 per request · Deducted from next month salary
              </div>
              {advances.length===0 ? (
                <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>No advance records.</div>
              ) : (
                <table>
                  <thead><tr><th>Date</th><th>Amount</th><th>Status</th><th>Deduct Month</th><th>Reason</th></tr></thead>
                  <tbody>
                    {advances.map((a,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{a.request_date}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:'var(--brand)' }}>{inr(a.amount)}</td>
                        <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:500, background:a.status==='APPROVED'?'#EAF3DE':a.status==='DEDUCTED'?'#E6F1FB':'#FDE8E8', color:a.status==='APPROVED'?'#3B6D11':a.status==='DEDUCTED'?'#185FA5':'#C0392B' }}>{a.status}</span></td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{a.deduct_month||'—'}</td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{a.reason||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* LEAVE TAB */}
          {detailTab==='Leave' && (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, flex:1 }}>Leave Records</div>
                <button className="btn btn-primary btn-sm" onClick={()=>setShowLeaveModal(true)}>+ Leave</button>
              </div>
              <div style={{ padding:'10px 14px', background:'#FDE8E8', borderRadius:'var(--radius-sm)', marginBottom:14, fontSize:12, color:'var(--red)', fontWeight:500 }}>
                ⚠️ All leaves deduct full day salary
              </div>
              {leaves.length===0 ? (
                <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>No leave records.</div>
              ) : (
                <table>
                  <thead><tr><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th>Reason</th></tr></thead>
                  <tbody>
                    {leaves.map((l,i)=>(
                      <tr key={i}>
                        <td style={{ fontSize:12, fontWeight:500 }}>{l.leave_type?.replace('_',' ')}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{l.from_date}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{l.to_date}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center', fontWeight:700 }}>{l.days}</td>
                        <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:500, background:l.status==='APPROVED'?'#EAF3DE':'#FDE8E8', color:l.status==='APPROVED'?'#3B6D11':'#C0392B' }}>{l.status}</span></td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{l.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* MARK ATTENDANCE MODAL */}
          {showAttModal && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:420, maxWidth:'90vw' }}>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Mark Attendance</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={attForm.date} max={new Date().toISOString().split('T')[0]} onChange={e=>setAttForm(f=>({...f,date:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">Status</label>
                    <select className="form-input" value={attForm.status} onChange={e=>setAttForm(f=>({...f,status:e.target.value}))}>
                      {['PRESENT','ABSENT','HALF_DAY','LEAVE','OVERTIME','HOLIDAY','WEEKLY_OFF'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Shift</label>
                    <select className="form-input" value={attForm.shift} onChange={e=>setAttForm(f=>({...f,shift:e.target.value}))}>
                      {['MORNING','AFTERNOON','NIGHT','FULL'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={attForm.notes} onChange={e=>setAttForm(f=>({...f,notes:e.target.value}))}/></div>
                </div>
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary" onClick={()=>setShowAttModal(false)}>Cancel</button>
                  <button className="btn btn-primary" disabled={tabSaving} onClick={async()=>{
                    setTabSaving(true);
                    try { await api.post('/employees/attendance/',{...attForm,employee:selected.id}); setShowAttModal(false); loadAttendance(selected.id,detailMonth); }
                    catch(e){ alert(e.response?.data?.errors?.non_field_errors?.[0]||'Failed'); }
                    finally { setTabSaving(false); }
                  }}>{tabSaving?'Saving...':'Save'}</button>
                </div>
              </div>
            </div>
          )}

          {/* ADVANCE MODAL */}
          {showAdvModal && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:420, maxWidth:'90vw' }}>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Salary Advance</div>
                <div style={{ padding:'10px 14px', background:'var(--brand-dim)', borderRadius:'var(--radius-sm)', marginBottom:14, fontSize:12, color:'var(--brand)' }}>Max ₹3,000 · Deducted next month</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  <div className="form-group"><label className="form-label">Amount (₹) — Max ₹3,000</label><input className="form-input" type="number" max="3000" step="0.01" value={advForm.amount} onChange={e=>setAdvForm(f=>({...f,amount:e.target.value}))} placeholder="0.00"/></div>
                  <div className="form-group"><label className="form-label">Deduct Month</label><input className="form-input" type="month" value={advForm.deduct_month} onChange={e=>setAdvForm(f=>({...f,deduct_month:e.target.value}))}/></div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">Reason</label><textarea className="form-input" rows={2} value={advForm.reason} onChange={e=>setAdvForm(f=>({...f,reason:e.target.value}))}/></div>
                </div>
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary" onClick={()=>setShowAdvModal(false)}>Cancel</button>
                  <button className="btn btn-primary" disabled={tabSaving} onClick={async()=>{
                    setTabSaving(true);
                    try { await api.post('/employees/advances/',{...advForm,employee:selected.id,status:'APPROVED'}); setShowAdvModal(false); loadDetailData(selected.id); }
                    catch(e){ alert(e.response?.data?.errors?.amount?.[0]||'Failed'); }
                    finally { setTabSaving(false); }
                  }}>{tabSaving?'Saving...':'Approve'}</button>
                </div>
              </div>
            </div>
          )}

          {/* LEAVE MODAL */}
          {showLeaveModal && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:420, maxWidth:'90vw' }}>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Add Leave</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  <div className="form-group"><label className="form-label">Leave Type</label>
                    <select className="form-input" value={leaveForm.leave_type} onChange={e=>setLeaveForm(f=>({...f,leave_type:e.target.value}))}>
                      {['SICK','EMERGENCY','PAID','UNPAID'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Status</label>
                    <select className="form-input" value={leaveForm.status} onChange={e=>setLeaveForm(f=>({...f,status:e.target.value}))}>
                      {['PENDING','APPROVED','REJECTED'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">From Date</label><input className="form-input" type="date" value={leaveForm.from_date} onChange={e=>setLeaveForm(f=>({...f,from_date:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">To Date</label><input className="form-input" type="date" value={leaveForm.to_date} onChange={e=>{
                    const days=Math.max(1,Math.ceil((new Date(e.target.value)-new Date(leaveForm.from_date))/(1000*60*60*24))+1);
                    setLeaveForm(f=>({...f,to_date:e.target.value,days}));
                  }}/></div>
                  <div className="form-group"><label className="form-label">Days</label><input className="form-input" type="number" min="1" value={leaveForm.days} onChange={e=>setLeaveForm(f=>({...f,days:e.target.value}))}/></div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">Reason</label><textarea className="form-input" rows={2} value={leaveForm.reason} onChange={e=>setLeaveForm(f=>({...f,reason:e.target.value}))}/></div>
                </div>
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary" onClick={()=>setShowLeaveModal(false)}>Cancel</button>
                  <button className="btn btn-primary" disabled={tabSaving} onClick={async()=>{
                    setTabSaving(true);
                    try { await api.post('/employees/leaves/',{...leaveForm,employee:selected.id}); setShowLeaveModal(false); loadDetailData(selected.id); }
                    catch{ alert('Failed to save leave.'); }
                    finally { setTabSaving(false); }
                  }}>{tabSaving?'Saving...':'Save'}</button>
                </div>
              </div>
            </div>
          )}
          {/* SALARY MODAL */}
          {showSalaryModal && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:520, maxWidth:'90vw', maxHeight:'90vh', overflowY:'auto' }}>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Pay Salary — {selected.full_name}</div>

                {/* Wage info */}
                <div style={{ padding:'10px 14px', background:'var(--brand-dim)', borderRadius:'var(--radius-sm)', marginBottom:14, fontSize:12, color:'var(--brand)', fontWeight:600 }}>
                  {selected.wage_type === 'DAILY'
                    ? `Daily Wage: ₹${selected.wage_amount}/day`
                    : `Monthly Salary: ₹${Number(selected.wage_amount||0).toLocaleString('en-IN')}/month`}
                  {pendingAdv > 0 && ` · Pending Advance: ₹${pendingAdv}`}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  {/* Payment Type */}
                  <div className="form-group">
                    <label className="form-label">Payment Type</label>
                    <select className="form-input" value={salaryForm.payment_type}
                      onChange={e=>setSalaryForm(f=>({...f,payment_type:e.target.value}))}>
                      {['MONTHLY','WEEKLY','DAILY','BONUS','DEDUCTION'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Month */}
                  <div className="form-group">
                    <label className="form-label">Month</label>
                    <input className="form-input" type="month" value={salaryForm.month}
                      onChange={e=>setSalaryForm(f=>({...f,month:e.target.value}))}/>
                  </div>

                  {/* Days Worked — auto calculates everything */}
                  <div className="form-group">
                    <label className="form-label">Days Worked</label>
                    <input className="form-input" type="number" min="0" value={salaryForm.days_worked}
                      onChange={e => {
                        const daysWorked = parseInt(e.target.value||0);
                        // Total days in selected month
                        const [yr, mo] = (salaryForm.month||currentMonth).split('-');
                        const totalDays = new Date(parseInt(yr), parseInt(mo), 0).getDate();
                        const daysAbsent = Math.max(0, totalDays - daysWorked);
                        // Calculate salary
                        let calculatedAmount = 0;
                        if(selected.wage_type === 'DAILY') {
                          calculatedAmount = daysWorked * parseFloat(selected.wage_amount||0);
                        } else {
                          const perDay = parseFloat(selected.wage_amount||0) / totalDays;
                          calculatedAmount = parseFloat(selected.wage_amount||0) - (daysAbsent * perDay);
                        }
                        calculatedAmount = Math.max(0, calculatedAmount);
                        // Pending advance
                        const pendingAdvAmt = advances.filter(a=>a.status==='APPROVED').reduce((s,a)=>s+parseFloat(a.amount||0),0);
                        setSalaryForm(f=>({
                          ...f,
                          days_worked: e.target.value,
                          days_absent: daysAbsent,
                          amount: calculatedAmount.toFixed(2),
                          advance_deducted: pendingAdvAmt.toFixed(2),
                        }));
                      }}/>
                  </div>

                  {/* Days Absent — auto */}
                  <div className="form-group">
                    <label className="form-label">Days Absent (Auto)</label>
                    <input className="form-input" readOnly value={salaryForm.days_absent||0}
                      style={{background:'var(--blue-bg)',color:'var(--brand)',fontWeight:700,cursor:'not-allowed'}}/>
                  </div>

                  {/* Amount — auto calculated */}
                  <div className="form-group">
                    <label className="form-label">Gross Amount (₹) — Auto</label>
                    <input className="form-input" readOnly value={salaryForm.amount||'0.00'}
                      style={{background:'var(--blue-bg)',color:'var(--brand)',fontWeight:700,cursor:'not-allowed'}}/>
                  </div>

                  {/* Payment Mode */}
                  <div className="form-group">
                    <label className="form-label">Payment Mode</label>
                    <select className="form-input" value={salaryForm.payment_mode}
                      onChange={e=>setSalaryForm(f=>({...f,payment_mode:e.target.value}))}>
                      {['CASH','BANK','CHEQUE','UPI'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Advance Deducted — auto */}
                  <div className="form-group">
                    <label className="form-label">Advance Deducted (₹) — Auto</label>
                    <input className="form-input" readOnly value={salaryForm.advance_deducted||'0.00'}
                      style={{background:'#FDE8E8',color:'var(--red)',fontWeight:700,cursor:'not-allowed'}}/>
                  </div>

                  {/* Other Deductions */}
                  <div className="form-group">
                    <label className="form-label">Other Deductions (₹)</label>
                    <input className="form-input" type="number" step="0.01" value={salaryForm.deductions}
                      onChange={e=>setSalaryForm(f=>({...f,deductions:e.target.value}))}/>
                  </div>

                  {/* Net Amount — auto */}
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Net Amount (₹) — Auto</label>
                    <input className="form-input" readOnly
                      value={`₹${Math.max(0, parseFloat(salaryForm.amount||0) - parseFloat(salaryForm.advance_deducted||0) - parseFloat(salaryForm.deductions||0)).toFixed(2)}`}
                      style={{background:'#EAF3DE',color:'var(--green)',fontWeight:700,fontSize:16,cursor:'not-allowed'}}/>
                  </div>

                  {/* Notes */}
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={2} value={salaryForm.notes}
                      onChange={e=>setSalaryForm(f=>({...f,notes:e.target.value}))}/>
                  </div>
                </div>

                <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                  <button className="btn btn-secondary" onClick={()=>setShowSalaryModal(false)}>Cancel</button>
                  <button className="btn btn-primary" disabled={tabSaving} onClick={async()=>{
                    setTabSaving(true);
                    try {
                      const net = Math.max(0, parseFloat(salaryForm.amount||0) - parseFloat(salaryForm.advance_deducted||0) - parseFloat(salaryForm.deductions||0));
                      await api.post('/employees/salary/', {
                        ...salaryForm,
                        employee: selected.id,
                        net_amount: net.toFixed(2),
                      });
                      // Mark advance as deducted if advance was deducted
                      if(parseFloat(salaryForm.advance_deducted||0) > 0) {
                        const pendingAdvances = advances.filter(a=>a.status==='APPROVED');
                        for(const adv of pendingAdvances) {
                          await api.patch(`/employees/advances/${adv.id}/`, {status:'DEDUCTED'});
                        }
                      }
                      setShowSalaryModal(false);
                      loadDetailData(selected.id);
                    } catch(e) {
                      alert(e.response?.data?.errors ? JSON.stringify(e.response.data.errors) : 'Failed to save salary.');
                    }
                    finally { setTabSaving(false); }
                  }}>{tabSaving?'Saving...':'Pay Now'}</button>
                </div>
              </div>
            </div>
          )}

        </div>
      </Layout>
    );
  }

  {selected?.documents?.length > 0 && (
   <div className="card" style={{marginBottom:12}}>
     <div className="card-header" style={{marginBottom:10}}>
       <div className="card-title">Uploaded Documents</div>
     </div>
     {selected.documents.map((doc,i)=>(
       <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}>
         <span style={{fontSize:13,fontWeight:500}}>{doc.doc_type} — {doc.title}</span>
         <a href={`http://127.0.0.1:8000${doc.file}`} target="_blank" rel="noreferrer" style={{fontSize:12,color:'var(--brand)',fontWeight:600}}>View File</a>
       </div>
     ))}
   </div>
 )}

  // ── Form View ──────────────────────────────────────────────────────────────
  if (view === 'form') {
    return (
      <Layout
        title={selected?.id ? 'Edit Employee' : 'Add New Employee'}
        subtitle={selected?.id ? `Editing: ${selected.employee_id}` : 'Employee ID auto-generated as TAPL/YYYY/001'}
        actions={<button className="btn btn-secondary btn-sm" onClick={() => setView('list')}>← Back to List</button>}
      >
        <div style={{ maxWidth:740 }}>
          {error && <div className="error-box">{error}</div>}
          {msg && <div className="success-box">{msg}</div>}

          <form onSubmit={e => handleSubmit(e, false)} autoComplete="off">

            {/* Section 1 — Basic */}
            <Section title="Basic Information">
              <Field label="Employee ID">
                <input className="form-input" value={selected?.employee_id||'Auto-generated'} readOnly style={{ opacity:0.5, cursor:'not-allowed' }}/>
              </Field>
              <Field label="First Name" required>
                <input className="form-input" required value={form.first_name} onChange={e => set('first_name',e.target.value)} placeholder="First name"/>
              </Field>
              <Field label="Last Name" required>
                <input className="form-input" required value={form.last_name} onChange={e => set('last_name',e.target.value)} placeholder="Last name"/>
              </Field>
              <Field label="Contact Number" required>
                <input className="form-input" required value={form.phone}
                  onChange={e => { if(/^\d{0,10}$/.test(e.target.value)) set('phone',e.target.value); }}
                  placeholder="10-digit mobile number" maxLength={10}/>
              </Field>
              <Field label="Emergency Contact Name">
                <input className="form-input" value={form.emergency_contact_name} onChange={e => set('emergency_contact_name',e.target.value)} placeholder="Emergency contact person"/>
              </Field>
              <Field label="Emergency Contact Number">
                <input className="form-input" value={form.alternate_phone}
                  onChange={e => { if(/^\d{0,10}$/.test(e.target.value)) set('alternate_phone',e.target.value); }}
                  placeholder="Emergency number" maxLength={10}/>
                <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:6, cursor:'pointer', fontSize:12, color:'var(--text-secondary)' }}>
                  <input type="checkbox" checked={sameContact} onChange={e => {
                    setSameContact(e.target.checked);
                    if (e.target.checked) set('alternate_phone', form.phone);
                  }} style={{ width:13, height:13 }}/>
                  Same as primary contact
                </label>
              </Field>
              <Field label="Email">
                <input className="form-input" type="email" value={form.email} onChange={e => set('email',e.target.value)} placeholder="Email address"/>
              </Field>
            </Section>

            {/* Section 2 — Personal */}
            <Section title="Personal Details">
              <Field label="Blood Group">
                <select className="form-input" value={form.blood_group} onChange={e => set('blood_group',e.target.value)}>
                  <option value="">Select blood group</option>
                  {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                </select>
              </Field>
              <Field label="Date of Birth">
                <input className="form-input" type="date" value={form.date_of_birth||''} onChange={e => set('date_of_birth',e.target.value)} max={new Date().toISOString().split('T')[0]}/>
              </Field>
              <Field label="Address" full>
                <textarea className="form-input" rows={2} value={form.address} onChange={e => set('address',e.target.value)} placeholder="Full residential address"/>
              </Field>
            </Section>

            {/* Section 3 — Address Proof Upload */}
            <Section title="Address Proof Document">
              <Field label="Document Type">
                <select className="form-input" value={addressDocType} onChange={e => setAddressDocType(e.target.value)}>
                  <option value="AADHAR">Aadhar Card</option>
                  <option value="PAN">PAN Card</option>
                  <option value="VOTER_ID">VOTER ID</option>
                  <option value="OTHER">Other</option>
                </select>
              </Field>
              <Field label="Aadhar Number">
                <input className="form-input" value={form.aadhar_number}
                  onChange={e => { if(/^\d{0,12}$/.test(e.target.value)) set('aadhar_number',e.target.value); }}
                  placeholder="12-digit Aadhar" maxLength={12}/>
              </Field>
              <Field label="PAN Number">
                <input className="form-input" value={form.pan_number}
                  onChange={e => set('pan_number',e.target.value.toUpperCase())}
                  placeholder="ABCDE1234F" maxLength={10}/>
              </Field>
              <Field label="Voter ID">
                <input className="form-input" value={form.voter_id||''} 
                onChange={e=>set('voter_id',e.target.value.toUpperCase())} 
                placeholder="Voter ID number"/>
              </Field>
              {form.documents && form.documents.filter(d => d.doc_type !== 'DRIVING_LICENSE').length > 0 && (
                <div style={{ gridColumn:'1/-1' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>Previously Uploaded</div>
                  {form.documents.filter(d => d.doc_type !== 'DRIVING_LICENSE').map((doc, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'var(--bg-input)', border:'1px solid var(--green-border)', borderRadius:'var(--radius-sm)', marginBottom:6 }}>
                      <span style={{ fontSize:13, color:'var(--text-primary)', fontWeight:500 }}>{doc.doc_type} — {doc.title}</span>
                      <a href={`http://127.0.0.1:8000${doc.file}`} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'var(--brand)', fontWeight:600 }}>View File</a>
                    </div>
                  ))}
                </div>
              )}
              <Field label="Upload Address Proof (JPG/PNG/PDF, max 2MB)" full>
                <div onClick={() => addressFileRef.current?.click()} style={{
                  border:`2px dashed ${addressDoc?'var(--green)':'var(--border)'}`,
                  borderRadius:'var(--radius-sm)', padding:16, cursor:'pointer',
                  textAlign:'center', background: addressDoc?'var(--green-bg)':'var(--bg-input)',
                  transition:'all 0.2s',
                }}>
                  <Upload size={20} style={{ color:addressDoc?'var(--green)':'var(--text-muted)', marginBottom:6 }}/>
                  <div style={{ fontSize:13, color:addressDoc?'var(--green)':'var(--text-muted)', fontWeight:addressDoc?600:400 }}>
                    {addressDoc ? `✓ ${addressDoc.name}` : 'Click to upload address proof'}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>JPG, PNG, PDF · Max 2MB</div>
                  {addressDoc && (
                    <button type="button" onClick={e => { e.stopPropagation(); setAddressDoc(null); if(addressFileRef.current) addressFileRef.current.value=''; }}
                      style={{ marginTop:8, fontSize:12, color:'var(--red)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
                      Remove
                    </button>
                  )}
                </div>
                <input ref={addressFileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display:'none' }}
                  onChange={e => {
                    const f = e.target.files[0];
                    if (!f) return;
                    if (f.size > 2*1024*1024) { setError('File size must be under 2MB'); return; }
                    setAddressDoc(f);
                  }}/>
              </Field>
            </Section>

            {/* Section 4 — Job Details */}
            <Section title="Job Details">
              <Field label="Role / Designation" required>
                <select className="form-input" required value={form.designation} onChange={e => set('designation',e.target.value)}>
                  <option value="">Select role</option>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Department">
                <input className="form-input" value={form.department} onChange={e => set('department',e.target.value)} placeholder="e.g. Quarry, Transport"/>
              </Field>
              <Field label="Skill Level">
                <select className="form-input" value={form.skill_level} onChange={e => set('skill_level',e.target.value)}>
                  {SKILL_LEVELS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Employment Type">
                <select className="form-input" value={form.employment_type||'PERMANENT'} onChange={e=>set('employment_type',e.target.value)}>
                  <option value="PERMANENT">Permanent</option>
                  <option value="CONTRACT">Contract</option>
                  <option value="DAILY_WAGE">Daily Wage</option>
                </select>
              </Field>
              
              {/* Show contract fields only when CONTRACT is selected */}
              {form.employment_type==='CONTRACT' && <>
                <Field label="Contractor Company">
                 <input className="form-input" value={form.contractor_company||''} onChange={e=>set('contractor_company',e.target.value)} placeholder="Company/Contractor name"/>
                </Field>
                <Field label="Vehicle Number">
                  <input className="form-input" value={form.vehicle_number||''} onChange={e=>set('vehicle_number',e.target.value)} placeholder="TN-XX-XXXX"/>
                </Field>
                <Field label="Contract Rate (₹)">
                  <input className="form-input" type="number" step="0.01" value={form.contract_rate||''} onChange={e=>set('contract_rate',e.target.value)} placeholder="0.00"/>
                </Field>
                <Field label="Rate Type">
                  <select className="form-input" value={form.contract_rate_type||''} onChange={e=>set('contract_rate_type',e.target.value)}>
                    <option value="">Select Rate Type</option>
                    <option value="PER_TRIP">Per Trip</option>
                    <option value="PER_DAY">Per Day</option>
                    <option value="PER_FEET">Per Feet</option>
                    <option value="FIXED">Fixed Amount</option>
                  </select>
                </Field>
              </>}
              <Field label="Biometric ID">
                <input className="form-input" value={form.biometric_id} onChange={e => set('biometric_id',e.target.value)} placeholder="Attendance device ID"/>
              </Field>
            </Section>

            {/* Section 5 — Driving License (CONDITIONAL) */}
            {isDriver(form.designation) && (
              <Section title="Driving License — Required for this Role">
                <div style={{ gridColumn:'1/-1' }}>
                  <div className="warning-box" style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <AlertTriangle size={14} style={{ flexShrink:0 }}/>
                    Driving License is mandatory for {form.designation}
                  </div>
                </div>
                <Field label="Driving License Number" required>
                  <input className="form-input" required value={form.driving_license} onChange={e => set('driving_license',e.target.value)} placeholder="License number"/>
                </Field>
                <Field label="License Expiry Date" required>
                  <input className="form-input" required type="date" value={form.license_expiry}
                    onChange={e => set('license_expiry',e.target.value)}
                    style={{ borderColor: licenseExpired(form.license_expiry)?'var(--red)':undefined }}/>
                  {licenseExpired(form.license_expiry) && (
                    <div style={{ fontSize:11, color:'var(--red)', marginTop:4, display:'flex', alignItems:'center', gap:4 }}>
                      <AlertTriangle size={11}/> License is EXPIRED
                    </div>
                  )}
                </Field>

                {/* Driving License Document Upload */}
                {form.documents && form.documents.filter(d => d.doc_type === 'DRIVING_LICENSE').length > 0 && (
                  <div style={{ gridColumn:'1/-1' }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>Previously Uploaded License</div>
                    {form.documents.filter(d => d.doc_type === 'DRIVING_LICENSE').map((doc, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'var(--bg-input)', border:'1px solid var(--green-border)', borderRadius:'var(--radius-sm)', marginBottom:6 }}>
                        <span style={{ fontSize:13, color:'var(--text-primary)', fontWeight:500 }}>Driving License</span>
                        <a href={`http://127.0.0.1:8000${doc.file}`} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'var(--brand)', fontWeight:600 }}>View File</a>
                      </div>
                    ))}
                  </div>
                )}
                <Field label="Upload Driving License (JPG/PNG/PDF, max 2MB)" full>
                  <div onClick={() => licenseFileRef.current?.click()} style={{
                    border:`2px dashed ${licenseDoc?'var(--green)':'var(--border)'}`,
                    borderRadius:'var(--radius-sm)', padding:16, cursor:'pointer',
                    textAlign:'center', background: licenseDoc?'var(--green-bg)':'var(--bg-input)',
                    transition:'all 0.2s',
                  }}>
                    <Upload size={20} style={{ color:licenseDoc?'var(--green)':'var(--text-muted)', marginBottom:6 }}/>
                    <div style={{ fontSize:13, color:licenseDoc?'var(--green)':'var(--text-muted)', fontWeight:licenseDoc?600:400 }}>
                      {licenseDoc ? `✓ ${licenseDoc.name}` : 'Click to upload driving license'}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>JPG, PNG, PDF · Max 2MB</div>
                    {licenseDoc && (
                      <button type="button" onClick={e => { e.stopPropagation(); setLicenseDoc(null); if(licenseFileRef.current) licenseFileRef.current.value=''; }}
                        style={{ marginTop:8, fontSize:12, color:'var(--red)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
                        Remove
                      </button>
                    )}
                  </div>
                  <input ref={licenseFileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display:'none' }}
                    onChange={e => {
                      const f = e.target.files[0];
                      if (!f) return;
                      if (f.size > 2*1024*1024) { setError('File size must be under 2MB'); return; }
                      setLicenseDoc(f);
                    }}/>
                </Field>
              </Section>
            )}

            {/* Section 6 — Salary */}
            <Section title="Salary Details">
              <Field label="Wage Type" required full>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                  {[['DAILY','Daily Wage'],['MONTHLY','Monthly Salary'],['PER_PRODUCTION','Per Production']].map(([val,label]) => (
                    <label key={val} style={{
                      display:'flex', alignItems:'center', gap:8, cursor:'pointer',
                      padding:'9px 16px', borderRadius:'var(--radius-sm)',
                      border:`1px solid ${form.wage_type===val?'var(--brand)':'var(--border)'}`,
                      background: form.wage_type===val?'var(--brand-dim)':'var(--bg-input)',
                      fontSize:13, fontWeight:500,
                      color: form.wage_type===val?'var(--brand)':'var(--text-secondary)',
                    }}>
                      <input type="radio" name="wage_type" value={val} checked={form.wage_type===val} onChange={e => set('wage_type',e.target.value)} style={{ width:14, height:14 }}/>
                      {label}
                    </label>
                  ))}
                </div>
              </Field>
              {form.wage_type==='DAILY' && (
                <Field label="Daily Wage (₹)" required>
                  <input className="form-input" type="number" required min="0" value={form.wage_amount} onChange={e => set('wage_amount',e.target.value)} placeholder="Amount per day"/>
                </Field>
              )}
              {form.wage_type==='MONTHLY' && (
                <Field label="Monthly Salary (₹)" required>
                  <input className="form-input" type="number" required min="0" value={form.wage_amount} onChange={e => set('wage_amount',e.target.value)} placeholder="Amount per month"/>
                </Field>
              )}
              {form.wage_type==='PER_PRODUCTION' && (
                <Field label="Rate per Unit (₹ per m³/feet)" required>
                  <input className="form-input" type="number" required min="0" value={form.rate_per_unit} onChange={e => set('rate_per_unit',e.target.value)} placeholder="Rate per unit"/>
                </Field>
              )}
              <Field label="Overtime Rate (₹/hr) — Optional">
                <input className="form-input" type="number" min="0" value={form.overtime_rate} onChange={e => set('overtime_rate',e.target.value)} placeholder="e.g. 100"/>
              </Field>
            </Section>

            {/* Section 7 — Employment */}
            <Section title="Employment Details">
              <Field label="Joining Date" required>
                <input className="form-input" required type="date" value={form.date_of_joining} onChange={e => set('date_of_joining',e.target.value)} max={new Date().toISOString().split('T')[0]}/>
              </Field>
              <Field label="Employment Status">
                <select className="form-input" value={form.is_active?'true':'false'} onChange={e => set('is_active',e.target.value==='true')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </Field>
              <Field label="PF Number">
                <input className="form-input" value={form.pf_number} onChange={e => set('pf_number',e.target.value)} placeholder="Provident Fund number"/>
              </Field>
              <Field label="ESI Number">
                <input className="form-input" value={form.esi_number} onChange={e => set('esi_number',e.target.value)} placeholder="ESI number"/>
              </Field>
            </Section>

            {/* Section 8 — Bank */}
            <Section title="Bank Details" defaultOpen={false}>
              <Field label="Bank Name">
                <input className="form-input" value={form.bank_name} onChange={e => set('bank_name',e.target.value)} placeholder="e.g. State Bank of India"/>
              </Field>
              <Field label="Account Holder Name">
                <input className="form-input" value={form.bank_account_name||''} onChange={e => set('bank_account_name',e.target.value)} placeholder="Name as per bank account"/>
              </Field>
              <Field label="Account Number">
                <input className="form-input" value={form.bank_account_number} onChange={e => { if(/^\d*$/.test(e.target.value)) set('bank_account_number',e.target.value); }} placeholder="Account number"/>
              </Field>
              <Field label="IFSC Code">
                <input className="form-input" value={form.bank_ifsc} onChange={e => set('bank_ifsc',e.target.value.toUpperCase())} placeholder="SBIN0001234" maxLength={11}/>
              </Field>
              <Field label="Branch Name">
                <input className="form-input" value={form.bank_branch||''} onChange={e => set('bank_branch',e.target.value)} placeholder="e.g. Anna Nagar Branch"/>
              </Field>
            </Section>

            {/* Bottom Action Buttons */}
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', padding:'20px 0', borderTop:'1px solid var(--border)', marginTop:8, flexWrap:'wrap' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setView('list')}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={saving}
                onClick={e => handleSubmit(e, true)}
              >
                {saving ? 'Saving...' : 'Save & Add New'}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Employee'}
              </button>
            </div>

          </form>
        </div>
      </Layout>
    );
  }

  // ── List View ──────────────────────────────────────────────────────────────
  return (
    <Layout
      title="Employees"
      subtitle="HR management and employee records"
      actions={<button className="btn btn-primary" onClick={openAdd}><Plus size={14}/>Add Employee</button>}
    >
      {msg && <div className="success-box">{msg}</div>}

      <div className="kpi-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:16 }}>
        <div className="kpi-card green"><div className="kpi-value">{employees.filter(e=>e.is_active).length}</div><div className="kpi-label">Active</div></div>
        <div className="kpi-card red"><div className="kpi-value">{employees.filter(e=>!e.is_active).length}</div><div className="kpi-label">Inactive</div></div>
        <div className="kpi-card amber">
          <div className="kpi-value">{employees.filter(e=>isDriver(e.designation)&&licenseExpired(e.license_expiry)).length}</div>
          <div className="kpi-label">License Expired</div>
        </div>
        <div className="kpi-card brand"><div className="kpi-value">{employees.length}</div><div className="kpi-label">Total</div></div>
      </div>

      <div className="card">
        <div className="filter-bar">
          <div className="search-wrap" style={{ flex:2 }}>
            <Search size={14}/>
            <input className="search-input" placeholder="Search by name, ID, phone..." value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <select className="form-input" style={{ width:'auto', minWidth:150 }} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="form-input" style={{ width:'auto', minWidth:130 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"/>Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="empty-state"><p>No employees found. Click "Add Employee" to get started.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Employee ID</th><th>Name</th><th>Role</th>
                  <th>Contact</th><th>Skill</th><th>Joining Date</th>
                  <th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees
                  .filter(e => !filterRole || e.designation===filterRole)
                  .map(emp => {
                    const expired = isDriver(emp.designation) && licenseExpired(emp.license_expiry);
                    return (
                      <tr key={emp.id} style={{ opacity:emp.is_active?1:0.6 }}>
                        <td style={{ color:'var(--brand)', fontWeight:600 }}>{emp.employee_id}</td>
                        <td>
                          <div style={{ fontWeight:600, color:'var(--text-primary)', display:'flex', alignItems:'center', gap:6 }}>
                            {emp.full_name}
                            {expired && <AlertTriangle size={13} style={{ color:'var(--red)' }} title="License Expired!"/>}
                          </div>
                        </td>
                        <td>{emp.designation||'—'}</td>
                        <td>{emp.phone}</td>
                        <td>{skillBadge(emp.skill_level)}</td>
                        <td>{emp.date_of_joining}</td>
                        <td>{statusBadge(emp)}</td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="btn btn-sm btn-secondary" title="View" onClick={() => openDetail(emp)}><Eye size={12}/></button>
                            <button className="btn btn-sm btn-secondary" title="Edit" onClick={() => openEdit(emp)}><Edit2 size={12}/></button>
                            <button className="btn btn-sm btn-secondary" title="Toggle Status" onClick={() => handleStatusChange(emp)}><RefreshCw size={12}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
